import React from 'react';
import {
    Card,
    AdvancedTable,
    AdvancedTableData,
    CustomSwitch,
    WarningModal,
    ExtendedSelect,
} from 'components';
import { Button, Form, Col } from 'react-bootstrap';
import { IApiError } from '_helpers/ApiConnector/types';
import styles from './Connected.module.scss';
import cn from 'classnames';

const Connected = ({
    getList,
    ecsList,
    ecsCatalog,
    getCatalog,
    addEc,
    setEmployee,
    deleteEc,
    onlyWatch,
    cardCategory,
    apiError,
    withBorder,
    showLoader,
}: IProps) => {
    const initialState: IState = {
        showSelectModal: false,
        showWarning: false,
        deletedId: null,
        listSortOrder: true,
        selectedQecEmployee: false,
        selectedQec: '',
    };

    const [showSelectModal, setShowSelectModal] = React.useState(initialState.showSelectModal);
    const [showWarning, setShowWarning] = React.useState(initialState.showWarning);
    const [deletedId, setDeletedId] = React.useState(initialState.deletedId);
    const [listSortOrder, setListSortOrder] = React.useState(initialState.listSortOrder);
    const [selectedQecEmployee, setSelectedQecEmployee] = React.useState<boolean>(initialState.selectedQecEmployee);
    const [selectedQec, setSelectedQec] = React.useState<string>(initialState.selectedQec);
    const [processingEmployee, setProcessingEmployee] = React.useState({ ecId: '', loading: false });

    const onOpenCatalog = async () => {
        if (getCatalog) {
            await getCatalog({ pageSize: 1000 });
            setShowSelectModal(true);
        }
    };

    const onCloseSelectModal = () => {
        setSelectedQec(initialState.selectedQec);
        setSelectedQecEmployee(initialState.selectedQecEmployee);
        setShowSelectModal(initialState.showSelectModal);
    };

    const onAddQecs = () => {
        if (selectedQec.length && addEc) {
            addEc({
                ecId: selectedQec,
                employee: selectedQecEmployee,
            });
        }
        onCloseSelectModal();
    };

    const onHideWarningModal = () => {
        setDeletedId(null);
        setShowWarning(false);
    };

    const onSubmitWarningModal = () => {
        if (deletedId && deleteEc) {
            deleteEc(deletedId);
        }
        setShowWarning(false);
    };

    const createHeader = () => {
        if (!onlyWatch) {
            return [
                { fieldName: 'ecRegNumber', title: 'ЭЦ ID' },
                { fieldName: 'ecName', title: 'НАЗВАНИЕ' },
                { fieldName: 'ecCity', title: 'РЕГИОН' },
                { fieldName: '', title: 'СОТРУДНИК' },
                { fieldName: '', title: '' },
            ];
        }

        return [
            { fieldName: 'ecRegNumber', title: 'ЭЦ ID' },
            { fieldName: 'ecName', title: 'НАЗВАНИЕ' },
            { fieldName: 'ecCity', title: 'РЕГИОН' },
            { fieldName: '', title: 'СОТРУДНИК' },
        ];
    };

    const checkAddedQec = (catalogQeqId: string): boolean => {
        if (ecsList && ecsList.items) {
            return ecsList && !!ecsList.items.find(item => item.ecId === catalogQeqId);
        }

        return false;
    };

    const handleListQecSelect = async (ecId: string, employer: boolean) => {
        if (setEmployee) {
            setProcessingEmployee({ ecId, loading: true });
            await setEmployee({ ecId, employer: !employer });
            setProcessingEmployee({ ecId, loading: false });
        }
    };

    const handleListOrder = (fieldName: string) => {
        setListSortOrder(!listSortOrder);
        getList({ sortField: fieldName, sortOrder: listSortOrder });
    };

    const handleListSearch = (fieldName: string, value: string) => getList({ searchField: fieldName, searchTerm: value });

    const renderListSwitch = (id: string, expertIsEmployee: boolean) => (
        <CustomSwitch
          onClick={() => !onlyWatch && handleListQecSelect(id, expertIsEmployee)}
          name="ec-list-selector"
          value={expertIsEmployee}
          status={onlyWatch ? 'accept' : 'default'}
          loading={processingEmployee.loading && processingEmployee.ecId === id}
          disabled={processingEmployee.loading}
          containerClassName={styles.switch}
          changed
        />
    );

    const renderDeleteButton = (ecId: string) => (
        <svg onClick={() => { setDeletedId(ecId); setShowWarning(true); }} data-test-delete-button style={{ cursor: 'pointer' }} width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path opacity="0.5" fillRule="evenodd" clipRule="evenodd" d="M8.50467 6.11171C8.62967 6.23671 8.69217 6.3885 8.69217 6.56707C8.69217 6.74564 8.62967 6.89743 8.50467 7.02243L7.59396 7.93314C7.46896 8.05814 7.31717 8.12064 7.1386 8.12064C6.96003 8.12064 6.80825 8.05814 6.68325 7.93314L4.71449 5.96439L2.74574 7.93314C2.62074 8.05814 2.46896 8.12064 2.29039 8.12064C2.11182 8.12064 1.96003 8.05814 1.83503 7.93314L0.924316 7.02243C0.799316 6.89743 0.736816 6.74564 0.736816 6.56707C0.736816 6.3885 0.799316 6.23671 0.924316 6.11171L2.89307 4.14296L0.924316 2.17421C0.799316 2.04921 0.736816 1.89743 0.736816 1.71885C0.736816 1.54028 0.799316 1.3885 0.924316 1.2635L1.83503 0.352783C1.96003 0.227783 2.11182 0.165283 2.29039 0.165283C2.46896 0.165283 2.62074 0.227783 2.74574 0.352783L4.71449 2.32153L6.68325 0.352783C6.80825 0.227783 6.96003 0.165283 7.1386 0.165283C7.31717 0.165283 7.46896 0.227783 7.59396 0.352783L8.50467 1.2635C8.62967 1.3885 8.69217 1.54028 8.69217 1.71885C8.69217 1.89743 8.62967 2.04921 8.50467 2.17421L6.53592 4.14296L8.50467 6.11171Z" fill="#EC2E18" />
        </svg>
    );

    const getCol = (ecId: string, ecRegNumber: string, ecName: string, ecCity: string, expertIsEmployee: boolean) => {
        if (!onlyWatch) {
            return [
                ecRegNumber,
                ecName,
                ecCity,
                renderListSwitch(ecId, expertIsEmployee),
                renderDeleteButton(ecId),
            ];
        }

        return [
            ecRegNumber,
            ecName,
            ecCity,
            renderListSwitch(ecId, expertIsEmployee),
        ];
    };

    const createListTableData = (ecs: IQuec[]): AdvancedTableData[] => ecs.map(({
        ecId, ecRegNumber, ecName, ecCity, expertIsEmployee,
    }) => ({
        id: ecId,
        columns: getCol(ecId, ecRegNumber, ecName, ecCity, expertIsEmployee),
    }));

    const disFunc = () => true;

    const warningModalProps = {
        title: 'Удалить связанный ЭЦ?',
        show: showWarning,
        onHide: onHideWarningModal,
        onAccess: onSubmitWarningModal,
    };

    const selectFilteredQecs = ecsCatalog ? ecsCatalog.items.filter(({ ecId }) => !checkAddedQec(ecId)) : [];
    const selectOptions = selectFilteredQecs.map(({ ecId, ecRegNumber, ecName }) => ({
        value: ecId,
        label: `${ecRegNumber}/${ecName}`,
    }));

    return (
        <Card
          cardClassName={cn(!withBorder && 'border-0')}
          contentClassName={cn(withBorder && 'mt-4')}
          title="Связанные ЭЦ"
          category={cardCategory || 'Список ЭЦ, с которыми сотрудничает эксперт'}
          apiError={apiError}
          showLoader={showLoader}
          withoutBottomMargin
          withoutPadding
        >
            {!onlyWatch && (<Button className={styles.add_button} onClick={onOpenCatalog}>Добавить связанный ЭЦ</Button>)}
            {ecsList && ecsList.items && (
                <>
                    <AdvancedTable
                      data={createListTableData(ecsList.items)}
                      tableHeaders={createHeader()}
                      handlePageChange={({ page, perPage }) => getList({ page, pageSize: perPage })}
                      handleOrder={handleListOrder}
                      handleRowClick={disFunc}
                      handleSearch={handleListSearch}
                      pageSize={ecsList.pageSize || 10}
                      total={ecsList.total || 1}
                      page={ecsList.page || 1}
                      placeholderTitle="Нет добавленных связанных ЭЦ"
                      placeholderText="Вам необходимо добавить, как минимум, один связанный ЭЦ"
                    />
                    {!onlyWatch && (<WarningModal {...warningModalProps} />)}
                </>
            )}
            <WarningModal
              show={showSelectModal}
              title="Выберите ЭЦ, сотрудничающий с экспертом"
              onHide={onCloseSelectModal}
              onAccess={onAddQecs}
            >
                {ecsCatalog ? (
                    <ExtendedSelect
                      name="region"
                      value={selectedQec}
                      options={selectOptions}
                      onChange={e => setSelectedQec(e.value)}
                      placeholder="Выбрать ЭЦ"
                      withoutDetectChanges
                    />
                ) : null}
                <Form.Group className={`row mt-4 ${styles.align_center}`}>
                    <Col sm="4" className="p-0">
                        <Form.Label column>
                            ЯВЛЯЕТСЯ СОТРУДНИКОМ
                        </Form.Label>
                    </Col>
                    <Col sm="5">
                        <CustomSwitch
                          onClick={() => setSelectedQecEmployee(!selectedQecEmployee)}
                          name="qeq-catalog-selector"
                          value={selectedQecEmployee}
                          status={selectedQecEmployee ? undefined : 'default'}
                        />
                    </Col>
                </Form.Group>
            </WarningModal>
        </Card>
    );
};

export default Connected;

export interface IProps {
    ecsList: IList | null;
    apiError: IApiError | null;
    ecsCatalog?: IList | null;
    onlyWatch?: boolean;
    cardCategory?: string;
    withBorder?: boolean;
    showLoader?: boolean;
    getList: (data: IGetListProps) => void;
    getCatalog?: (data: IGetCatalogProps) => void;
    addEc?: (addedEc: IAddEc) => void;
    setEmployee?: (data: ISetEmployee) => void;
    deleteEc?: (ecId: string) => void;
}

export interface IList {
    items: IQuec[];
    page: number;
    pageSize: number;
    total: number;
}

export interface IGetCatalogProps {
    onlyAssigned?: boolean;
    searchField?: string;
    searchTerm?: string;
    page?: number;
    pageSize?: number;
    sortOrder?: boolean;
    sortField?: string;
}

export interface IGetListProps {
    expertIdQuery?: string;
    searchField?: string;
    searchTerm?: string;
    page?: number;
    pageSize?: number;
    sortOrder?: boolean;
    sortField?: string;
}

export interface ISetEmployee {
    ecId: string;
    employer: boolean;
}

interface IState {
    showSelectModal: boolean;
    showWarning: boolean;
    deletedId: string | null;
    listSortOrder: boolean;
    selectedQecEmployee: boolean;
    selectedQec: string;
}

interface IQuec {
    ecId: string;
    ecRegNumber: string;
    ecName: string;
    ecCity: string;
    expertIsEmployee: boolean;
}

interface IAddEc {
    ecId: string;
    employee: boolean;
}
